import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader, AlertTriangle, Check, RefreshCw, PenLine, Undo2 } from 'lucide-react';
import { neonQuery, todayET } from '../services/api';

// HANDWRITTEN NOTES - the packing list and follow-up board behind the note in every non-VIP box.
//
// Rachael's spec, 2026-09-30. Two views:
//   Packing list - the day's non-VIP shipments. Pick the rep who packed the note (and the
//                  template); that customer is now hers to follow up, nothing else to do.
//   Follow-ups   - Due today (overdue included) / Upcoming / Completed, per rep.
//
// ⚠️ ANYONE ALREADY IN THE VIP PROCESS IS HELD BACK, NOT HIDDEN. Her hard requirement: two reps
// must never reach the same customer with two messages. They stay countable behind a toggle with
// the reason, same rule as the win-back tab. Boundary and logic: netlify/functions/lib/note-followups.js.

const TEMPLATES = ['Template A', 'Template B', 'Birthday/Occasion', 'First-Time Customer', 'Repeat Customer', 'Other'];

const BTN = (bg, border, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6,
  fontSize: 12, fontWeight: 600, background: bg, border: '1px solid ' + border, color,
  cursor: 'pointer', fontFamily: 'inherit',
});
const PILL = (on) => ({
  ...BTN(on ? 'var(--navy)' : '#fff', on ? 'var(--navy)' : 'var(--gray-300)', on ? '#fff' : 'var(--gray-700)'),
});
const TH = { padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-500)', borderBottom: '1px solid var(--gray-200)', whiteSpace: 'nowrap' };
const TD = { padding: '8px 10px', borderBottom: '1px solid var(--gray-100)', fontSize: 13, verticalAlign: 'top' };
const SEL = { padding: '5px 6px', fontSize: 12.5, fontFamily: 'inherit', borderRadius: 6, border: '1px solid var(--gray-300)' };

const firstName = (n) => String(n || '').split(' ')[0];
const md = (iso) => (iso ? Number(String(iso).slice(5, 7)) + '/' + Number(String(iso).slice(8, 10)) : '');
const mdTs = (ts) => { if (!ts) return ''; const d = new Date(ts); return (d.getMonth() + 1) + '/' + d.getDate(); };
const shift = (iso, n) => { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

export default function NoteFollowupPanel({ rep }) {
  const [view, setView] = useState('pack');
  const [reps, setReps] = useState([]);
  useEffect(() => {
    neonQuery('outreach_assignees', {}).then((r) => setReps((r || []).filter((x) => x.recent_sends > 0 || String(x.admin_id) === String(rep && rep.adminId))))
      .catch(() => setReps([]));
  }, [rep]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        <button style={PILL(view === 'pack')} onClick={() => setView('pack')}>Packing list</button>
        <button style={PILL(view === 'follow')} onClick={() => setView('follow')}>Follow-ups</button>
      </div>
      {view === 'pack' ? <PackingList rep={rep} reps={reps} /> : <FollowUps rep={rep} reps={reps} />}
    </div>
  );
}

// ── Packing list ─────────────────────────────────────────────────────────────────────────────
function PackingList({ rep, reps }) {
  const today = todayET();
  const [date, setDate] = useState(today);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [showHeld, setShowHeld] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [draft, setDraft] = useState({});   // orderId -> {repAdminId, template, notes}
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    setRows(null); setErr(null);
    try { setRows(await neonQuery('note_packing_list', { date })); }
    catch (e) { setErr(e.message); setRows([]); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  // AN UN-NOTED ROW SHOWS NO REP. It used to pre-fill with whoever was signed in,
  // so Linda saw "Linda" on every row and Ashley saw "Ashley" on the same rows -
  // nothing was saved, but it read as CPx having assigned everyone to everybody
  // (Rachael, 2026-10-12). The reps split the day's sheets by hand first, so a
  // row belongs to nobody until somebody clicks Note added.
  const d = (r) => draft[r.orderId] || {
    repAdminId: r.note ? String(r.note.repAdminId) : '',
    template: r.note ? (r.note.template || '') : (r.previous ? 'Repeat Customer' : ''),
    notes: r.note ? (r.note.notes || '') : '',
  };
  const patch = (r, p) => setDraft((m) => ({ ...m, [r.orderId]: { ...d(r), ...p } }));

  const save = async (r) => {
    const x = { ...d(r) };
    // Left blank, Note added means "I packed it": the person signed in.
    if (!x.repAdminId && rep && rep.adminId != null) x.repAdminId = String(rep.adminId);
    if (!x.repAdminId) { setErr('Pick the rep who packed the note.'); return; }
    setSaving(r.orderId); setErr(null);
    try {
      await neonQuery('note_assign', {
        orderId: r.orderId, orderNumber: r.orderNumber, customerId: r.customerId, customerName: r.name,
        email: r.email, shipDate: r.shipDate, repAdminId: x.repAdminId, template: x.template || null,
        notes: x.notes, byName: rep && rep.name,
      });
      setDraft((m) => { const n = { ...m }; delete n[r.orderId]; return n; });
      await load();
    } catch (e) { setErr(e.message); } finally { setSaving(null); }
  };
  const undo = async (r) => {
    if (!window.confirm(`Take the note off order ${r.orderNumber}? The follow-up is cancelled.`)) return;
    try { await neonQuery('note_unassign', { orderId: r.orderId, byName: rep && rep.name }); await load(); }
    catch (e) { setErr(e.message); }
  };

  const list = rows || [];
  const held = list.filter((r) => r.held);
  const workable = list.filter((r) => !r.held);
  const noted = workable.filter((r) => r.note);
  const shown = (showHeld ? held : workable)
    .filter((r) => !mineOnly || (r.note && String(r.note.repAdminId) === String(rep && rep.adminId)));

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-600)' }}>Shipping on</span>
        <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} style={SEL} />
        <button style={PILL(date === shift(today, -1))} onClick={() => setDate(shift(today, -1))}>Yesterday</button>
        <button style={PILL(date === today)} onClick={() => setDate(today)}>Today</button>
        <button style={PILL(date === shift(today, 1))} onClick={() => setDate(shift(today, 1))}>Tomorrow</button>
        <button style={BTN('#fff', 'var(--gray-300)', 'var(--gray-600)')} onClick={load}><RefreshCw size={12} /> Refresh</button>
      </div>

      {rows && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, fontSize: 13, color: 'var(--gray-600)' }}>
          <b style={{ color: 'var(--navy)' }}>{workable.length}</b> non-VIP shipments
          <span>&middot;</span>
          <span style={{ color: '#0b8043', fontWeight: 600 }}>{noted.length} noted</span>
          <span>&middot;</span>
          <span>{workable.length - noted.length} to go</span>
          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
            &middot; Nobody is assigned until Note added is clicked. Leave Packed by blank and it goes to you.
          </span>
          <div style={{ flex: 1 }} />
          <button style={PILL(mineOnly)} onClick={() => setMineOnly((v) => !v)}>Mine only</button>
          {held.length > 0 && (
            <button style={PILL(showHeld)} onClick={() => setShowHeld((v) => !v)}
              title="Already in the VIP process - a note follow-up would be a second contact">
              <AlertTriangle size={12} /> {held.length} held back (VIP)
            </button>
          )}
        </div>
      )}

      {showHeld && (
        <div style={{ fontSize: 12.5, color: '#975a16', background: '#fffaf0', border: '1px solid #f6e05e', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
          These customers are already being handled through a VIP offer, so they are left off the packing list to avoid two reps
          reaching out with two different messages. If you put a note in anyway, check with the rep named first.
        </div>
      )}
      {err && <div style={{ color: '#b42318', fontSize: 13, marginBottom: 10 }}>{err}</div>}
      {!rows ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)' }}><Loader size={16} className="spin" /> Loading shipments...</div>
      ) : !shown.length ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)' }}>
          {mineOnly ? 'None of your notes on this day.' : 'No non-VIP shipments on this day.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>Customer</th><th style={TH}>Order</th><th style={TH}>Delivery</th>
                <th style={TH}>Packed by</th><th style={TH}>Template</th><th style={TH}>Notes</th><th style={TH} />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const x = d(r);
                const dirty = !!draft[r.orderId];
                return (
                  <tr key={r.orderId} style={{ background: r.note && !dirty ? '#f0fff4' : undefined }}>
                    <td style={TD}>
                      <div style={{ fontWeight: 700, color: 'var(--navy)' }}>{r.name}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>{[r.city, r.state].filter(Boolean).join(', ')}</div>
                      {r.previous && (
                        /* Repeat customer: enough to choose a different rep and template. */
                        <div style={{ marginTop: 4, fontSize: 11.5, fontWeight: 600, color: '#b7791f' }}>
                          <AlertTriangle size={11} /> Note {mdTs(r.previous.notedAt)} by {firstName(r.previous.repName)}
                          {r.previous.template ? ' · ' + r.previous.template : ''}
                          {r.previous.orderNumber ? ' (#' + r.previous.orderNumber + ')' : ''}
                        </div>
                      )}
                      {r.held && <div style={{ marginTop: 4, fontSize: 11.5, fontWeight: 600, color: '#975a16' }}>{r.heldLabel}</div>}
                    </td>
                    <td style={TD}>
                      #{r.orderNumber}
                      <div style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>ships {md(r.shipDate)}</div>
                    </td>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>{md(r.deliveryDate)}{r.delivered ? ' ✓' : ''}</td>
                    <td style={TD}>
                      <select style={SEL} value={x.repAdminId} onChange={(e) => patch(r, { repAdminId: e.target.value })}>
                        <option value="">Not assigned</option>
                        {reps.map((p) => <option key={p.admin_id} value={String(p.admin_id)}>{firstName(p.staff_name)}</option>)}
                      </select>
                    </td>
                    <td style={TD}>
                      <select style={SEL} value={x.template} onChange={(e) => patch(r, { template: e.target.value })}>
                        <option value="">Template…</option>
                        {TEMPLATES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </td>
                    <td style={TD}>
                      <input style={{ ...SEL, width: 170 }} placeholder="optional" value={x.notes}
                        onChange={(e) => patch(r, { notes: e.target.value })} />
                    </td>
                    <td style={{ ...TD, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {r.note && !dirty ? (
                        <>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#0b8043' }}><Check size={12} /> {firstName(r.note.repName)}</span>
                          <button onClick={() => undo(r)} title="Take the note off" style={{ border: 'none', background: 'none', color: 'var(--gray-500)', cursor: 'pointer', marginLeft: 6 }}><Undo2 size={13} /></button>
                        </>
                      ) : (
                        <button disabled={saving === r.orderId} onClick={() => save(r)} style={BTN('#fff3ed', 'var(--orange)', 'var(--orange)')}>
                          {saving === r.orderId ? <Loader size={12} className="spin" /> : <PenLine size={12} />} {r.note ? 'Save' : 'Note added'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Follow-ups ───────────────────────────────────────────────────────────────────────────────
function FollowUps({ rep, reps }) {
  const today = todayET();
  const [who, setWho] = useState(rep && rep.adminId != null ? String(rep.adminId) : '');
  const [b, setB] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try { setB(await neonQuery('note_board', { today, repAdminId: who || null })); }
    catch (e) { setErr(e.message); setB({ dueToday: [], upcoming: [], later: [], completed: [] }); }
  }, [today, who]);
  useEffect(() => { load(); }, [load]);

  const contacted = async (it) => {
    const note = window.prompt(`Mark ${it.name} (order #${it.orderNumber}) as contacted? How did it go? (optional)`, '');
    if (note === null) return;
    try { await neonQuery('note_contacted', { id: it.id, note, byName: rep && rep.name }); await load(); }
    catch (e) { setErr(e.message); }
  };
  const reopen = async (it) => {
    try { await neonQuery('note_contacted', { id: it.id, undo: true }); await load(); }
    catch (e) { setErr(e.message); }
  };
  const setOffset = async () => {
    const cur = b && b.config ? b.config.offset_days : 3;
    const v = window.prompt('Follow up how many days after delivery?', String(cur));
    if (v === null || !/^\d+$/.test(v.trim())) return;
    try { await neonQuery('note_config_set', { patch: { offset_days: Number(v) }, byName: rep && rep.name }); await load(); }
    catch (e) { setErr(e.message); }
  };

  const Section = ({ title, items, tone, done }) => (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: tone, marginBottom: 6 }}>{title} <span style={{ color: 'var(--gray-400)', fontWeight: 600 }}>({items.length})</span></div>
      {!items.length ? <div style={{ fontSize: 12.5, color: 'var(--gray-400)', padding: '4px 0 8px' }}>Nothing here.</div> : (
        <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={TH}>Order</th><th style={TH}>Customer</th><th style={TH}>Delivery</th>
              <th style={TH}>{done ? 'Contacted' : 'Follow up'}</th><th style={TH}>Rep</th><th style={TH}>Template</th><th style={TH}>Notes</th><th style={TH} />
            </tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td style={TD}>#{it.orderNumber}</td>
                  <td style={TD}><div style={{ fontWeight: 700, color: 'var(--navy)' }}>{it.name}</div><div style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>{it.email}</div></td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{md(it.deliveryDate)}{it.delivered ? ' ✓' : <span style={{ color: 'var(--gray-400)' }}> (sched.)</span>}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap', color: !done && it.due && it.due < today ? '#b42318' : undefined, fontWeight: !done && it.due && it.due < today ? 700 : 400 }}>
                    {done ? mdTs(it.contactedAt) : md(it.due)}{!done && it.due && it.due < today ? ' (late)' : ''}
                  </td>
                  <td style={TD}>{firstName(it.repName)}</td>
                  <td style={TD}>{it.template || <span style={{ color: 'var(--gray-400)' }}>—</span>}</td>
                  <td style={{ ...TD, maxWidth: 260 }}>{done ? (it.contactNote || it.notes || '') : (it.notes || '')}</td>
                  <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {done && it.contactedByEmail
                      ? <span title="Ticked off by the Delivered by day email. Undo that send to reopen it." style={{ fontSize: 11, color: 'var(--gray-500)' }}>Emailed{it.contactedBy ? ' by ' + firstName(it.contactedBy) : ''}</span>
                      : done
                      ? <button onClick={() => reopen(it)} style={{ border: 'none', background: 'none', color: 'var(--gray-500)', fontSize: 11, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit' }}>Reopen</button>
                      : <button onClick={() => contacted(it)} style={BTN('#fff3ed', 'var(--orange)', 'var(--orange)')}><Check size={12} /> Contacted</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const upcomingAll = useMemo(() => (b ? b.upcoming : []), [b]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <button style={PILL(who === '')} onClick={() => setWho('')}>Everyone</button>
        {reps.map((p) => (
          <button key={p.admin_id} style={PILL(who === String(p.admin_id))} onClick={() => setWho(String(p.admin_id))}>{firstName(p.staff_name)}</button>
        ))}
        <div style={{ flex: 1 }} />
        {b && b.config && (
          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
            Follow up {b.config.offset_days} days after delivery{' '}
            <button onClick={setOffset} style={{ border: 'none', background: 'none', color: 'var(--orange)', fontSize: 12, textDecoration: 'underline', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>change</button>
          </span>
        )}
      </div>
      {err && <div style={{ color: '#b42318', fontSize: 13, marginBottom: 10 }}>{err}</div>}
      {!b ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)' }}><Loader size={16} className="spin" /> Loading...</div>
      ) : (
        <>
          <Section title="Due today" items={b.dueToday} tone="#b42318" />
          <Section title="Upcoming" items={upcomingAll} tone="var(--navy)" />
          {/* Rachael, Oct 22, 2026: a late, reshipped or Delay Tracker order gets no follow-up. */}
          {(b.delayed || []).length > 0 && <Section title="Held: the order was delayed, so no follow-up" items={b.delayed} tone="var(--gray-500)" />}
          <Section title="Completed (last 30 days)" items={b.completed} tone="#0b8043" done />
        </>
      )}
    </div>
  );
}
